import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { awaitDispatch } from '@vk-hr-tek/core/redux';
import { showError, showNotification } from '@vk-hr-tek/core/notifications';
import {
  GetCompanyUnitTreeDto,
  GetUnitTypesDto,
  UnitService,
  UnitType,
} from '@vk-hr-tek/core/units';

import { ThunkExtra } from '@app/store';
import { GetUnitsTreeResponse, GetUnitTypesResponse } from '@app/gen/settings';
import {
  PolicyCompany,
  CompanyPolicyListItem as CompanyPolicy,
  EmployeePolicyListItem as EmployeePolicy,
} from '@app/gen/policy';

import { CreationMapper } from '../../mappers';
import { PolicyRouter } from '../../types';
import { PolicyCreateService, PolicyDetailService } from '../../services';
import { GetPolicyDetailDto, UpdatePolicyDto } from '../../dto';
import { PolicyState } from '../policy.state';

import {
  getCompanyHasPositions,
  getPolicyHasEmployees,
} from './creation.actions';
import { getPolicyDetail } from './detail.actions';

const getCompanies = createAsyncThunk<PolicyCompany[], undefined, ThunkExtra>(
  'editPolicy/getCompanies',
  async (_, { extra: { inject }, rejectWithValue }) => {
    try {
      const res = await inject(PolicyCreateService).getCompanies();

      return res.companies;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const getCompanyUnitTypes = createAsyncThunk<
  GetUnitTypesResponse,
  GetUnitTypesDto,
  ThunkExtra
>(
  'editPolicy/getCompanyUnitTypes ',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const unitService = inject(UnitService);
      const unitTypes = await unitService.getUnitTypes(dto);

      return inject(CreationMapper).policyUnitTypeSorting(unitTypes);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEditPolicyCompanyUnitTree = createAsyncThunk<
  GetUnitsTreeResponse,
  GetCompanyUnitTreeDto,
  ThunkExtra
>(
  'editPolicy/getEditPolicyCompanyUnitTree',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const unitService = inject(UnitService);

      return await unitService.getUnitTree(dto as GetCompanyUnitTreeDto);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEditData = createAsyncThunk<
  void,
  GetPolicyDetailDto,
  ThunkExtra
>(
  'policy/getEditData',
  async (getPolicyDetailDto, { rejectWithValue, dispatch, getState }) => {
    try {
      if (!getState().policy.detail.entity) {
        await dispatch(getPolicyDetail(getPolicyDetailDto));
      }

      const detailEntity = getState().policy.detail.entity;

      const unitIds =
        detailEntity?.units
          .filter((item) => item.id)
          .map((unit) => unit.id || '') || [];

      await Promise.all([
        awaitDispatch(dispatch(getCompanies())),
        awaitDispatch(
          dispatch(
            getCompanyUnitTypes({
              companyId: detailEntity?.company.company_id || '',
            }),
          ),
        ),
        awaitDispatch(
          dispatch(
            getEditPolicyCompanyUnitTree({
              companyId: detailEntity?.company.company_id || '',
              unitType: (detailEntity?.unit_type || 'legal') as UnitType,
            }),
          ),
        ),
        awaitDispatch(
          dispatch(
            getCompanyHasPositions({
              companyId: detailEntity?.company.company_id || '',
              unitIds,
            }),
          ),
        ),
        awaitDispatch(
          dispatch(
            getPolicyHasEmployees({
              companyId: detailEntity?.company.company_id || '',
              unitIds,
            }),
          ),
        ),
      ]);

      if (detailEntity?.units.length) {
        await Promise.all(
          detailEntity.units.map((unit) =>
            awaitDispatch(
              dispatch(
                getEditPolicyCompanyUnitTree({
                  companyId: detailEntity?.company.company_id || '',
                  unitType: (detailEntity?.unit_type || 'legal') as UnitType,
                  unitId: unit.id,
                }),
              ),
            ),
          ),
        );
      }
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updatePolicy = createAsyncThunk<
  void,
  {
    values: UpdatePolicyDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'policy/updatePolicy',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(PolicyDetailService);
      const router = inject<PolicyRouter>(PolicyRouter);

      await service.updatePolicy(values);

      actions.resolve(null);

      dispatch(getPolicyDetail({ id: values.id }));

      router.goToDetail(values.id);

      dispatch(showNotification('Изменения сохранены'));
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showError('Не удалось опубликовать документ\nПопробуйте еще раз позже'),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const editReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<CompanyPolicy | EmployeePolicy, string> & PolicyState
  >,
) => {
  builder.addCase(getCompanies.pending, (state) => {
    state.edit.companies.status = 'loading';
  });
  builder.addCase(getCompanies.fulfilled, (state, { payload }) => {
    state.edit.companies.items = payload;
    state.edit.companies.status = 'complete';
  });
  builder.addCase(getCompanies.rejected, (state) => {
    state.edit.companies.status = 'failed';
  });
  builder.addCase(getCompanyUnitTypes.pending, (state) => {
    state.edit.unitTypes.status = 'loading';
  });
  builder.addCase(getCompanyUnitTypes.fulfilled, (state, { payload }) => {
    state.edit.unitTypes.status = 'complete';
    state.edit.unitTypes.values = payload;
  });
  builder.addCase(getCompanyUnitTypes.rejected, (state) => {
    state.edit.unitTypes.status = 'failed';
  });
  builder.addCase(getEditData.pending, (state) => {
    state.edit.status = 'loading';
  });
  builder.addCase(getEditData.fulfilled, (state) => {
    state.edit.status = 'complete';
  });
  builder.addCase(getEditData.rejected, (state, { payload, error }) => {
    state.edit.status = 'failed';
    state.edit.error =
      payload ||
      ({
        info: (error && error.message) || 'Что-то пошло не так',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(
    getEditPolicyCompanyUnitTree.fulfilled,
    (state, { payload, meta }) => {
      if (meta.arg.unitId) {
        state.edit.unitsTrees[meta.arg.unitId] = payload;
      } else {
        state.edit.unitsTree = payload;
      }
    },
  );
};
